import type { ReactNode } from 'react'
import { useState } from 'react'
import { Alert } from './Alert.js'
import { Dialog } from './Dialog.js'
import { Field } from './Field.js'

/**
 * Naming the way a board is arranged, so it becomes a tab.
 *
 * One dialog for both boards. What a view *holds* differs — the contacts board
 * keeps a rule tree, the segments board a search term — but naming one is the
 * same act, and so is what can go wrong with it: the name is taken, or the
 * request did not land.
 *
 * It saves what is on screen rather than opening a builder, because the board
 * *is* the builder. Somebody who has narrowed a list to what they want has
 * already done the work, and a second screen asking them to describe it again
 * is where a saved view stops being worth saving.
 *
 * **Saving a view creates nothing a campaign can be sent to.** The caller does
 * the saving; this only asks for the name.
 */
export function SaveViewDialog({
  keeps,
  onSave,
  onClose,
}: {
  /**
   * What the view will hold, in words.
   *
   * Said plainly, because a view that quietly dropped one of the things it
   * keeps is one somebody opens later and finds rearranged.
   */
  readonly keeps: ReactNode
  /** Saves under this name. `name-taken` is an answer, not a failure. */
  readonly onSave: (name: string) => Promise<'saved' | 'name-taken'>
  readonly onClose: () => void
}) {
  const [name, setName] = useState('')
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    setProblem(undefined)
    try {
      if ((await onSave(name.trim())) === 'name-taken') {
        setProblem('A view with that name already exists.')
        return
      }
      onClose()
    } catch {
      setProblem('We could not save that view. Please try again in a moment.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog heading="Save this view" onClose={onClose}>
      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <form
        id="save-view"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <Field
          disabled={saving}
          hint="It becomes a tab on this board. Everyone in your organisation sees it."
          id="view-name"
          label="Name"
          onChange={setName}
          type="text"
          value={name}
        />
      </form>

      <p className="mt-4 text-[12px] text-t3">Keeps {keeps}.</p>

      <div className="mt-5 flex items-center gap-3 border-line border-t pt-4">
        <button
          className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          disabled={saving || name.trim() === ''}
          form="save-view"
          type="submit"
        >
          {saving ? 'Saving…' : 'Save view'}
        </button>
        <button
          className="font-medium text-[13px] text-t3 hover:text-t1"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
      </div>
    </Dialog>
  )
}
